import {t} from 'i18next';
import React, {useState, useLayoutEffect, useRef} from 'react';

import {InputSize, ValidationStatus} from 'common/components/input/types';
import {TagInputReact} from 'common/components/tag-input';
import {TagInputChangeAction, TagInputChangeEvent, TagInputSearchMode} from 'common/components/tag-input/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useAnnotationTags} from 'common/contexts/react/annotation-tags-context';
import {AnnotationTag} from 'common/report/model-resources/annotation-tags/types';
import {react2panel} from 'common/util/react2panel';

type Props = {
  selectedTags: {[id: number]: AnnotationTag};
  onTagSelected: (tag: AnnotationTag) => void;
  onInputCleared: () => void;
  onTagDeleted: (tag: AnnotationTag) => void;
};

const MAX_TAG_LENGTH = 175; // this is controlled by the database

export function AnnotationTagInputReact({selectedTags, onTagSelected, onTagDeleted, onInputCleared}: Props) {
  const [searchString, setSearchString] = useState(``);
  const searchRef = useRef(searchString);
  useLayoutEffect(() => {
    searchRef.current = searchString;
  }, [searchString]);
  const annotationTagsResource = useAnnotationTags();
  const tags = annotationTagsResource.getSortedTags();

  const handleSearchInput = (searchFilter: string) => {
    setSearchString(searchFilter);
  };

  const handleSearchInputChange = (ev: TagInputChangeEvent) => {
    const {detail} = ev;
    if (detail.action === TagInputChangeAction.Text) {
      handleSearchInput(detail.value);
    } else if (detail.action === TagInputChangeAction.RemoveTag) {
      onTagDeleted(detail.tag as TODO);
    }
  };

  const getErrorMessage = () => {
    if (searchRef.current.length > MAX_TAG_LENGTH) {
      return t(`chart-actions.annotations.tag-length-error`, {limit: MAX_TAG_LENGTH, length: searchRef.current.length});
    }
    return ``;
  };

  const createNewTag = async () => {
    if (!getErrorMessage()) {
      await annotationTagsResource
        .createAnnotationTag({name: searchRef.current})
        .then((tag) => {
          setSearchString(``);
          onTagSelected(tag);
        })
        .catch(() => addToast({message: `Failed to create tag`, type: ToastType.ERROR}));
    }
  };
  return (
    <TagInputReact
      fieldId='annotation-tags'
      label={t(`chart-actions.annotations.tag-label`)}
      showClear
      errorMessage={getErrorMessage()}
      validationStatus={getErrorMessage() ? ValidationStatus.Invalid : ValidationStatus.Default}
      menuSections={[
        {
          label: t(`chart-actions.annotations.select-tag`).toUpperCase(),
          alwaysShowLabel: true,
          isLoading: tags === null,
          items: tags?.map((tag) => {
            return {
              label: tag.name,
              value: tag.id,
              id: tag.id,
              onSelect: () => onTagSelected(tag),
              isDisabled: !!selectedTags[tag.id],
            };
          }),
        },
        searchString
          ? {
              label: t(`chart-actions.annotations.create-new`).toUpperCase(),
              alwaysShowLabel: true,
              items: [
                {
                  icon: `plus`,
                  label: searchString,
                  onSelect: createNewTag,
                  ignoreSearchFilter: true,
                  highlightItem: false,
                  excludeFromInputMatch: true,
                  isSpecialAction: true,
                },
              ],
            }
          : {items: []},
      ]}
      mpItemsMenuAttrs={{
        'empty-message': t(`chart-actions.annotations.no-tags-help`),
      }}
      value={searchString}
      controlledMode
      size={InputSize.Large}
      multiline
      minTags={0}
      tags={Object.values(selectedTags).map((tag) => ({label: tag.name, ...tag}))} //label is used by <Tag> for display
      searchMode={TagInputSearchMode.Text}
      openOnRefresh
      searchWidth={100}
      onChange={(ev: TODO) => handleSearchInputChange(ev)}
      onClear={onInputCleared}
    />
  );
}

export const AnnotationTagInputEl = react2panel(`mp-annotation-tag-input`, AnnotationTagInputReact);
